import React from 'react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch from './hooks/useFetch'
import { loadUserLanguage } from './i18n/language'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import lightTheme from './theme'
import LoginPage from './pages/LoginPage'
import Dashboard from './pages/Dashboard'
import { BlockingLoadProvider } from './loading/BlockingLoad'

export default function App() {
    return (
        <BlockingLoadProvider>
            <AppContent />
        </BlockingLoadProvider>
    )
}

function AppContent() {
    const [auth, setAuth] = useState({
        isLoading: true,
        isLogged: false,
        username: '',
        perms: {
            // Customers, Products, Orders
            CPO: {
                all: false,
                customers: {
                    create: true,
                    update: true,
                    delete: false
                },
                products: {
                    create: true,
                    update: true,
                    delete: false
                },
                orders: {
                    create: false,
                    update: true,
                    delete: false,
                    returns: false
                }
            },

            // Accounting
            ACC: {
                all: true,
                accsPayable: false,
                expenses: false
            },

            // Admin Settings
            ADMIN: false
        }
    })

    const { fetchResource, isPending } = useFetch({ setAuth });
    const { i18n } = useTranslation()

    // The device language covers the login screen; once we know who signed in,
    // their own preference wins. A user who never chose keeps what is showing.
    useEffect(() => {
        if (!auth.isLogged || !auth.username) return
        const preferred = loadUserLanguage(auth.username)
        if (preferred && preferred !== i18n.language) i18n.changeLanguage(preferred)
    }, [auth.isLogged, auth.username, i18n])


    useEffect(() => { fetchResource({ method: 'GET', loadingKey: 'session' }) }, []);

    if (auth.isLoading || isPending('session')) return null;
    
    // The provider wraps both branches. It used to wrap only the dashboard, so
    // the login screen -- the first thing anyone sees -- rendered on MUI's stock
    // theme: different type, different blue, different radius.
    return (
      <ThemeProvider theme={lightTheme}>
        {/* Paints the ground colour on <body> and takes the browser's default
            margins and font off. Without it the app's own surfaces sat on white. */}
        <CssBaseline />
        {auth.isLogged
          ? <Dashboard auth={auth} setAuth={setAuth} />
          : <LoginPage setAuth={setAuth} fetchResource={fetchResource} />}
      </ThemeProvider>
    );
}
